import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  message,
  Space,
  Spin,
  Divider,
  Collapse,
} from "antd";
import RuleBuilderForm from "./RuleBuilderForm";
import { createApprovalTemplate, fetchApprovalTemplates, fetchFactCatalogue, fetchEntityTypes } from "../../api";
import { useRoles } from "../../../../hooks/useRoles";

const { TextArea } = Input;
const { Panel } = Collapse;


const ApprovalTemplateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  // Roles are dynamic — a role created from any RoleSelect (HR employees, users)
  // must be selectable as a step approver here without a code change.
  const { options: roleOptions, loading: rolesLoading } = useRoles();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    module: "",
    description: "",
    steps: [
      {
        stepNumber: 1,
        stepName: "",
        role: "",
        deadlineHours: 24,
      },
    ],
    rules: { conditions: { all: [] }, event: { type: "template-match" } },
  });
  const [rulePreview, setRulePreview] = useState("{}");
  const [messageApi, contextHolder] = message.useMessage();
  const [availableFacts, setAvailableFacts] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);

  // Fetch facts and entity types on mount
  useEffect(() => {
    fetchFactCatalogue()
      .then((facts) => setAvailableFacts(facts))
      .catch((err) => console.error("Failed to load facts:", err));
      
    fetchEntityTypes()
      .then((types) => setEntityTypes(types))
      .catch((err) => console.error("Failed to load entity types:", err));
  }, []);

  // Load template if editing
  useEffect(() => {
    if (id) {
      setLoading(true);
      fetchApprovalTemplates()
        .then((templates) => {
          const tpl = (templates || []).find((t) => t._id === id);
          if (tpl) {
            setFormData({
              name: tpl.name,
              module: tpl.module,
              description: tpl.description,
              steps: (tpl.steps || []).map((step, idx) => ({
                stepNumber: step.stepNumber || idx + 1,
                stepName: step.stepName,
                role: step.role,
                deadlineHours: step.deadlineHours || 24,
              })),
              rules: tpl.rules || { conditions: { all: [] }, event: { type: "template-match" } },
            });
            form.setFieldsValue({
              name: tpl.name,
              module: tpl.module,
              description: tpl.description,
              steps: (tpl.steps || []).map((step, idx) => ({
                stepName: step.stepName,
                role: step.role,
                deadlineHours: step.deadlineHours || 24,
              })),
            });
            setRulePreview(JSON.stringify(tpl.rules || {}, null, 2));
          } else {
            messageApi.error("Template not found");
            navigate("/approvals/approval-templates");
          }
        })
        .catch(() => messageApi.error("Failed to fetch template"))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line
  }, [id]);

  // Handle RuleBuilderForm changes
  const handleRuleChange = (rules) => {
    setFormData((prev) => ({ ...prev, rules }));
    setRulePreview(JSON.stringify(rules, null, 2));
  };

  // Handle form field changes
  const handleFormChange = (_, allValues) => {
    setFormData((prev) => ({
      ...prev,
      ...allValues,
      steps: (allValues.steps || []).map((step, idx) => ({
        ...step,
        stepNumber: idx + 1,
      })),
    }));
  };

  // Save handler
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!formData.rules || !formData.rules.conditions) {
        messageApi.error("Please define approval rules.");
        return;
      }
      setSaving(true);
      const payload = {
        name: values.name,
        module: values.module,
        description: values.description,
        steps: (values.steps || []).map((step, idx) => ({
          stepNumber: idx + 1,
          stepName: step.stepName,
          role: step.role,
          deadlineHours: step.deadlineHours || 24,
        })),
        rules: formData.rules,
      };
      await createApprovalTemplate(payload);
      messageApi.success("Approval template saved!");
      navigate("/approvals/approval-templates");
    } catch (err) {
      messageApi.error(
        err?.response?.data?.message || "Failed to save approval template"
      );
    }
    setSaving(false);
  };

  // Reset handler
  const handleReset = () => {
    form.resetFields();
    setFormData({
      name: "",
      module: "",
      description: "",
      steps: [
        {
          stepNumber: 1,
          stepName: "",
          role: "",
          deadlineHours: 24,
        },
      ],
      rules: { conditions: { all: [] }, event: { type: "template-match" } },
    });
    setRulePreview("{}");
  };

  // Cancel handler
  const handleCancel = () => {
    navigate("/approvals/approval-templates");
  };

  if (loading) return <Spin style={{ margin: 40 }} />;

  return (
    <Card
      title={id ? "Edit Approval Template" : "Create Approval Template"}
      style={{ maxWidth: 800, margin: "auto" }}
    >
      {contextHolder}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          name: formData.name,
          module: formData.module,
          description: formData.description,
          steps: formData.steps.map((s) => ({
            stepName: s.stepName,
            role: s.role,
            deadlineHours: s.deadlineHours,
          })),
        }}
        onValuesChange={handleFormChange}
      >
        <Form.Item
          label="Template Name"
          name="name"
          rules={[{ required: true, message: "Please enter template name" }]}
        >
          <Input placeholder="Enter template name" />
        </Form.Item>
        <Form.Item
          label="Module"
          name="module"
          rules={[{ required: true, message: "Please select module" }]}
        >
          <Select 
            options={entityTypes.map(e => ({ 
              label: e.displayName || e.entityType || e, 
              value: e.entityType || e 
            }))} 
            placeholder="Select module" 
          />
        </Form.Item>
        <Form.Item label="Description" name="description">
          <TextArea placeholder="Template description" />
        </Form.Item>
        <Divider />
        <Form.Item label="Approval Steps">
          <Form.List name="steps" initialValue={formData.steps}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, idx) => (
                  <Card
                    key={key}
                    type="inner"
                    title={`Step ${idx + 1}`}
                    style={{ marginBottom: 16 }}
                    extra={
                      fields.length > 1 ? (
                        <Button danger size="small" onClick={() => remove(name)}>
                          Remove
                        </Button>
                      ) : null
                    }
                  >
                    <Form.Item
                      {...restField}
                      label="Step Name"
                      name={[name, "stepName"]}
                      rules={[{ required: true, message: "Step name required" }]}
                    >
                      <Input placeholder="e.g. Manager Approval" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      label="Role"
                      name={[name, "role"]}
                      rules={[{ required: true, message: "Role required" }]}
                    >
                      <Select showSearch optionFilterProp="label" options={roleOptions} loading={rolesLoading} placeholder="Select role" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      label="Deadline (hours)"
                      name={[name, "deadlineHours"]}
                      rules={[{ required: true, type: "number", min: 1, message: "Enter deadline in hours" }]}
                    >
                      <Input type="number" min={1} placeholder="24" />
                    </Form.Item>
                  </Card>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon="+"
                  >
                    Add Step
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form.Item>
        <Divider />
        <Form.Item label="Approval Rules (json-rules-engine)">
          <RuleBuilderForm
            value={formData.rules}
            onChange={handleRuleChange}
            factOptions={(() => {
              const selectedModule = form.getFieldValue("module") || formData.module;
              
              // Always include standard/fallback facts
              const standardFacts = [
                { label: "Amount", value: "amount", type: "number" },
                { label: "Branch", value: "branch", type: "text" },
                { label: "Region", value: "region", type: "text" },
                { label: "Customer Type", value: "customerType", type: "text" },
              ];

              const filtered = availableFacts.filter(f => f.entityType === selectedModule);
              const extraFacts = filtered.map(f => ({ label: f.label, value: f.key, type: f.type || "text" }));

              // Combine and deduplicate
              const allOptions = [...standardFacts, ...extraFacts];
              return Array.from(new Map(allOptions.map(f => [f.value, f])).values());
            })()}
          />

          <Collapse style={{ marginTop: 12 }}>
            <Panel header="Live Rule JSON Preview" key="1">
              <pre style={{ background: "#f6f6f6", padding: 12, fontSize: 13 }}>
                {rulePreview}
              </pre>
            </Panel>
          </Collapse>
        </Form.Item>
        <Divider />
        <Space>
          <Button type="primary" onClick={handleSave} loading={saving}>
            Save Template
          </Button>
          <Button onClick={handleReset} disabled={saving}>
            Reset
          </Button>
          <Button onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
        </Space>
      </Form>
    </Card>
  );
};

export default ApprovalTemplateDetail;
